import { useEffect, useState, useCallback } from 'react'
import { Loader2, Bug, Lightbulb, ChevronDown, ChevronUp, Users, Clock, RefreshCw, Pencil, Trash2, Check, X } from 'lucide-react'
import { clsx } from 'clsx'
import { useFeedbackStore } from '../store'
import type { FeedbackTicket, KanbanStatus, AppendHistoryEntry, TicketPriority, TicketType } from '../types'
import { KANBAN_COLUMNS, PRIORITY_ORDER } from '../types'
import { formatDate } from '@utils/format'
import { showToast } from '@store/uiStore'

// ─── Ticket Card ────────────────────────────────────────────

function TicketCard({ ticket }: { ticket: FeedbackTicket }) {
  const { updateTicket, deleteTicket } = useFeedbackStore()
  const [expanded, setExpanded] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(ticket.title)
  const [editPriority, setEditPriority] = useState<TicketPriority>(ticket.priority)
  const [editType, setEditType] = useState<TicketType>(ticket.type)
  const [confirmDelete, setConfirmDelete] = useState(false)

  let appendHistory: AppendHistoryEntry[] = []
  try {
    appendHistory = JSON.parse(ticket.append_history_json || '[]')
  } catch {
    appendHistory = []
  }

  const appendCount = appendHistory.length

  const handleSaveEdit = async () => {
    await updateTicket(ticket.id, {
      title: editTitle,
      priority: editPriority,
      type: editType,
    })
    setIsEditing(false)
    showToast('Ticket updated', 'success')
  }

  const handleCancelEdit = () => {
    setEditTitle(ticket.title)
    setEditPriority(ticket.priority)
    setEditType(ticket.type)
    setIsEditing(false)
  }

  const handleDelete = async () => {
    const deleted = await deleteTicket(ticket.id)
    if (deleted) {
      showToast('Ticket deleted', 'success')
    } else {
      showToast('Failed to delete ticket', 'error')
    }
    setConfirmDelete(false)
  }

  return (
    <div
      draggable={!isEditing}
      onDragStart={(e) => {
        if (isEditing) { e.preventDefault(); return }
        e.dataTransfer.setData('ticketId', ticket.id.toString())
        e.dataTransfer.effectAllowed = 'move'
      }}
      className={clsx(
        'bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-lg p-3 transition-all shadow-sm hover:shadow-md',
        !isEditing && 'cursor-grab active:cursor-grabbing hover:border-primary/30'
      )}
    >
      {/* Delete Confirmation */}
      {confirmDelete ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-red-500">Delete this ticket?</p>
          <p className="text-[10px] text-[var(--text-muted)]">This cannot be undone.</p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
            >
              <Trash2 size={10} />
              Delete
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs px-2 py-1 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : isEditing ? (
        /* Edit Mode */
        <div className="space-y-2">
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="w-full text-sm font-medium px-2 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary/50"
            autoFocus
          />
          <div className="flex items-center gap-2">
            <select
              value={editType}
              onChange={(e) => setEditType(e.target.value as TicketType)}
              className="text-[10px] px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none"
            >
              <option value="bug">Bug</option>
              <option value="feature">Feature</option>
            </select>
            <select
              value={editPriority}
              onChange={(e) => setEditPriority(e.target.value as TicketPriority)}
              className="text-[10px] px-1.5 py-1 rounded border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleSaveEdit}
              disabled={!editTitle.trim()}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              <Check size={10} />
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X size={10} />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        /* View Mode */
        <>
          {/* Header */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              {ticket.type === 'bug' ? (
                <Bug size={14} className="text-red-500 flex-shrink-0" />
              ) : (
                <Lightbulb size={14} className="text-purple-500 flex-shrink-0" />
              )}
              <span className="text-sm font-medium text-[var(--text-primary)] truncate">
                {ticket.title}
              </span>
            </div>
            <span className={clsx(
              'text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 uppercase tracking-wide',
              ticket.priority === 'critical' && 'bg-red-500/10 text-red-500',
              ticket.priority === 'high' && 'bg-orange-500/10 text-orange-500',
              ticket.priority === 'medium' && 'bg-blue-500/10 text-blue-500',
              ticket.priority === 'low' && 'bg-gray-500/10 text-gray-500',
            )}>
              {ticket.priority}
            </span>
          </div>

          {/* AI Summary */}
          {ticket.ai_summary && (
            <p className="text-xs text-[var(--text-muted)] mt-1.5 line-clamp-2">
              {ticket.ai_summary}
            </p>
          )}

          {/* Meta */}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-[var(--text-muted)]">
            <span className="flex items-center gap-1">
              <Clock size={10} />
              {formatDate(ticket.created_at)}
            </span>
            {appendCount > 0 && (
              <span className="flex items-center gap-1 text-amber-500">
                <Users size={10} />
                +{appendCount} report{appendCount > 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Actions row */}
          <div className="flex items-center justify-between mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setExpanded(!expanded)
              }}
              className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              {expanded ? 'Less' : 'More'}
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsEditing(true)
                }}
                className="p-1 rounded text-[var(--text-muted)] hover:text-primary hover:bg-primary/10 transition-colors"
                title="Edit ticket"
              >
                <Pencil size={11} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setConfirmDelete(true)
                }}
                className="p-1 rounded text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors"
                title="Delete ticket"
              >
                <Trash2 size={11} />
              </button>
            </div>
          </div>

          {/* Expanded Details */}
          {expanded && (
            <div className="mt-2 pt-2 border-t border-[var(--border-color)] space-y-2">
              <div>
                <p className="text-[10px] font-medium text-[var(--text-muted)] uppercase">Original Report</p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">{ticket.description}</p>
              </div>
              <div className="text-[10px] text-[var(--text-muted)]">
                Reported by {ticket.submitted_by_name || ticket.submitted_by}
              </div>
              {appendHistory.length > 0 && (
                <div>
                  <p className="text-[10px] font-medium text-[var(--text-muted)] uppercase">Additional Reports</p>
                  <div className="space-y-1 mt-1">
                    {appendHistory.map((entry, idx) => (
                      <div key={idx} className="text-xs text-[var(--text-secondary)] bg-[var(--bg-secondary)] rounded p-2">
                        <span className="font-medium">{entry.user_name || entry.user_email}:</span>{' '}
                        {entry.description}
                        <span className="text-[10px] text-[var(--text-muted)] ml-2">
                          {formatDate(entry.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ─── Kanban Column ──────────────────────────────────────────

function KanbanColumn({
  status,
  label,
  tickets,
  onDrop,
}: {
  status: KanbanStatus
  label: string
  tickets: FeedbackTicket[]
  onDrop: (ticketId: number, newStatus: KanbanStatus) => void
}) {
  const [isDragOver, setIsDragOver] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const ticketId = parseInt(e.dataTransfer.getData('ticketId'))
    if (!isNaN(ticketId)) {
      onDrop(ticketId, status)
    }
  }, [onDrop, status])

  const columnColors: Record<KanbanStatus, string> = {
    backlog: 'border-t-gray-500',
    in_progress: 'border-t-blue-500',
    done: 'border-t-green-500',
  }

  // Sort by priority (critical first)
  const sortedTickets = [...tickets].sort(
    (a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]
  )

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={clsx(
        'flex-1 min-w-[280px] bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] border-t-4 flex flex-col',
        columnColors[status],
        isDragOver && 'ring-2 ring-primary/30 bg-primary/5'
      )}
    >
      {/* Column Header */}
      <div className="px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{label}</h3>
        <span className="text-xs bg-[var(--bg-tertiary)] text-[var(--text-muted)] px-2 py-0.5 rounded-full">
          {tickets.length}
        </span>
      </div>

      {/* Cards */}
      <div className="flex-1 px-3 pb-3 space-y-2 overflow-y-auto max-h-[calc(100vh-380px)]">
        {sortedTickets.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)]">
            <p className="text-xs">No tickets</p>
          </div>
        ) : (
          sortedTickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} />
          ))
        )}
      </div>
    </div>
  )
}

// ─── Roadmap Kanban Board ───────────────────────────────────

export function RoadmapKanban() {
  const { tickets, isLoading, loadTickets, updateTicketStatus } = useFeedbackStore()

  useEffect(() => {
    loadTickets()
  }, [loadTickets])

  const handleDrop = useCallback((ticketId: number, newStatus: KanbanStatus) => {
    updateTicketStatus(ticketId, newStatus)
  }, [updateTicketStatus])

  if (isLoading && tickets.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-[var(--text-muted)]" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--text-muted)]">
            Drag and drop tickets to update their status. Tickets are created via the feedback button.
          </p>
        </div>
        <button
          onClick={() => loadTickets()}
          disabled={isLoading}
          className="flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] px-2 py-1 rounded hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          <RefreshCw size={12} className={clsx(isLoading && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {KANBAN_COLUMNS.map((column) => (
          <KanbanColumn
            key={column.id}
            status={column.id}
            label={column.label}
            tickets={tickets.filter(t => t.status === column.id)}
            onDrop={handleDrop}
          />
        ))}
      </div>

      {/* Empty state */}
      {tickets.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-4xl mb-3">📋</div>
          <h3 className="text-lg font-medium text-[var(--text-primary)] mb-1">No feedback yet</h3>
          <p className="text-sm text-[var(--text-muted)]">
            Tickets will appear here when users submit feedback via the feedback button in the bottom-right corner.
          </p>
        </div>
      )}
    </div>
  )
}
